import React from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  TextField,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Divider,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Tooltip from '@mui/material/Tooltip';
import dayjs from 'dayjs';
import NumberFormatCustom from '../../globals/utils/Bond Creation Utils/numberFormatCustom';
import EndorsementPrincipalPickerFields from '../components/endorsement/EndorsementPrincipalPickerFields';
import { getPrincipalPickerConfig } from '../components/endorsement/endorsementPrincipalPickerConfig';
import BondDialogDocumentsSection from '../components/BondDialogDocumentsSection';
import EndorsementDwbNameFields from '../components/endorsement/EndorsementDwbNameFields';
import {
  getCancellationInstructionsList,
  getEndorsementInstructions,
} from '../Law Firm User/helpers/lawFirmBondsCancellationCopy';
import {
  PG_BONDS_ENDORSEMENT_TYPE_OPTIONS,
  PG_BONDS_VALID_DOCUMENT_TYPES,
  formatPgBondAmount,
  isEndorsementSubmitDisabled,
  isNameChangeEndorsement,
  transformPgBondStatusForDisplay,
} from './pgBondsTableConfig';

function renderAutocompleteOption(props, option) {
  const key = typeof option === 'string' ? option : option.value;
  const label = typeof option === 'string' ? option : option.label;
  return (
    <li {...props} key={key}>
      {label}
    </li>
  );
}

export function PgBondsDataTable({
  loading,
  tableColumns,
  orderBy,
  order,
  handleSort,
  searchFilteredData,
  currentRows,
  role,
  onViewBond,
  onCancelBond,
  onEndorseBond,
}) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ borderRadius: '8px' }}>
      <Table>
        <TableHead>
          <TableRow style={{ backgroundColor: '#f0f0f0' }}>
            {tableColumns.map((col) => (
              <TableCell key={col.key} sortDirection={orderBy === col.key ? order : false}>
                <TableSortLabel
                  active={orderBy === col.key}
                  direction={orderBy === col.key ? order : 'asc'}
                  onClick={() => handleSort(col.key)}
                >
                  {col.label}
                </TableSortLabel>
              </TableCell>
            ))}
            <TableCell align="center">Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody sx={{ '& tr:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
          {searchFilteredData.length === 0 ? (
            <TableRow>
              <TableCell colSpan={tableColumns.length + 1} align="center">
                <Typography variant="h6" sx={{ textAlign: 'center' }}>
                  No Data Available
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            currentRows.map((bond) => (
              <TableRow key={bond.bond_id ?? bond.bond_number}>
                <TableCell>{bond.bond_number}</TableCell>
                <TableCell>{bond.bond_type}</TableCell>
                <TableCell>{bond.bond_sub_category}</TableCell>
                <TableCell>{bond.principals.join(', ')}</TableCell>
                <TableCell>{bond.estate_name}</TableCell>
                <TableCell>{bond.law_firm_name}</TableCell>
                <TableCell>{bond.attorney_name}</TableCell>
                {(role === 'internal_user' || role === 'super_user') && (
                  <TableCell>{bond.managed_by.join(', ')}</TableCell>
                )}
                <TableCell>{formatPgBondAmount(bond.bond_amount)}</TableCell>
                <TableCell>{transformPgBondStatusForDisplay(bond.status)}</TableCell>
                <TableCell>{dayjs(bond.created_at).format('DD MMM YYYY')}</TableCell>
                <TableCell align="center">
                  <Tooltip title="View Bond Details">
                    <IconButton color="primary" onClick={() => onViewBond(bond)}>
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Cancel Bond">
                    <IconButton color="error" onClick={() => onCancelBond(bond)}>
                      <CancelIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Bond Endorsement">
                    <IconButton color="secondary" onClick={() => onEndorseBond(bond)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function PgBondsFilterDialog({
  open,
  onClose,
  filters,
  setFilters,
  applyFilters,
  clearFilters,
  bondTypes,
  availableSubcategories,
  allBonds,
  availablePrincipals,
  availableEstates,
  lawFirms,
  attorneys,
  internalUsers,
  role,
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Apply Filters</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        <Autocomplete
          multiple
          options={bondTypes}
          value={filters.bondType}
          onChange={(e, v) => setFilters((f) => ({ ...f, bondType: v }))}
          getOptionLabel={(o) => o.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          renderOption={renderAutocompleteOption}
          renderInput={(p) => <TextField {...p} label="Bond Type" />}
        />
        <Autocomplete
          multiple
          options={availableSubcategories}
          value={filters.bondSubCategory}
          onChange={(e, v) => setFilters((f) => ({ ...f, bondSubCategory: v }))}
          renderInput={(p) => <TextField {...p} label="Bond Sub Category" />}
        />
        <Autocomplete
          multiple
          options={[...new Set(allBonds.map((b) => b.bond_number).filter(Boolean))].map((num) => ({
            label: num,
            value: num,
          }))}
          value={filters.bondNumber}
          onChange={(e, v) => setFilters((f) => ({ ...f, bondNumber: v }))}
          getOptionLabel={(o) => o.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          renderOption={renderAutocompleteOption}
          renderInput={(p) => <TextField {...p} label="Bond Number" />}
        />
        <Autocomplete
          multiple
          options={availablePrincipals}
          value={filters.principal}
          onChange={(e, v) => setFilters((f) => ({ ...f, principal: v }))}
          getOptionLabel={(o) => o.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          renderOption={renderAutocompleteOption}
          renderInput={(p) => <TextField {...p} label="Principal" />}
        />
        <Autocomplete
          multiple
          options={availableEstates}
          value={filters.estate}
          onChange={(e, v) => setFilters((f) => ({ ...f, estate: v }))}
          getOptionLabel={(o) => o.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          renderOption={renderAutocompleteOption}
          renderInput={(p) => <TextField {...p} label="Estate / Ward / Trust / Beneficiary" />}
        />
        <Autocomplete
          multiple
          options={lawFirms}
          value={filters.lawFirm}
          onChange={(e, v) => setFilters((f) => ({ ...f, lawFirm: v }))}
          getOptionLabel={(o) => o.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          renderOption={renderAutocompleteOption}
          renderInput={(p) => <TextField {...p} label="Law Firm" />}
        />
        <Autocomplete
          multiple
          options={attorneys}
          value={filters.attorney}
          onChange={(e, v) => setFilters((f) => ({ ...f, attorney: v }))}
          getOptionLabel={(o) => o.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          renderOption={renderAutocompleteOption}
          renderInput={(p) => <TextField {...p} label="Attorney" />}
        />
        {(role === 'internal_user' || role === 'super_user') && (
          <Autocomplete
            multiple
            options={internalUsers}
            value={filters.internalUser}
            onChange={(e, v) => setFilters((f) => ({ ...f, internalUser: v }))}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            renderOption={renderAutocompleteOption}
            renderInput={(p) => <TextField {...p} label="Managed By" />}
          />
        )}
        <Autocomplete
          multiple
          options={['Active', 'Rejected', 'To Be Cancelled', 'Cancelled']}
          value={filters.status}
          onChange={(e, v) => setFilters((f) => ({ ...f, status: v }))}
          renderOption={renderAutocompleteOption}
          renderInput={(p) => <TextField {...p} label="Status" />}
        />
        <TextField
          type="date"
          label="Start Date"
          value={filters.startDate || ''}
          onChange={(e) => setFilters((f) => ({ ...f, startDate: e.target.value }))}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          type="date"
          label="End Date"
          value={filters.endDate || ''}
          onChange={(e) => setFilters((f) => ({ ...f, endDate: e.target.value }))}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={clearFilters}>Clear</Button>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={applyFilters}>
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function PgBondsCancellationDialog({
  open,
  onClose,
  selectedBond,
  cancellationFile,
  isCancelling,
  onFileChange,
  onSubmit,
}) {
  return (
    <Dialog open={open} onClose={() => !isCancelling && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: '1.3rem' }}>Bond Cancellation</DialogTitle>
      <DialogContent dividers>
        {selectedBond && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
              Cancellation Details
            </Typography>
            <Typography sx={{ mb: 1 }}>
              <strong>Bond Number:</strong> {selectedBond.bond_number ?? 'N/A'}
            </Typography>
            <Typography sx={{ mb: 2 }}>
              <strong>Bond Type:</strong> {selectedBond.bond_type}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <BondDialogDocumentsSection
              bond={selectedBond}
              getCancellationInstructionsList={getCancellationInstructionsList}
              cancellationFile={cancellationFile}
              disableUpload={isCancelling}
              onFileInputChange={onFileChange}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ padding: 2 }}>
        <Button onClick={onClose} disabled={isCancelling}>
          Close
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!cancellationFile || isCancelling}
          onClick={onSubmit}
        >
          {isCancelling ? <CircularProgress size={24} color="inherit" /> : 'Submit Cancellation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function renderPgDwbFields(props) {
  return (
    <EndorsementDwbNameFields
      selectedBondToEndorse={props.selectedBond}
      updatedName={props.updatedName}
      setUpdatedName={props.setUpdatedName}
      dwbFirstName={props.dwbFirstName}
      setDwbFirstName={props.setDwbFirstName}
      dwbMiddleName={props.dwbMiddleName}
      setDwbMiddleName={props.setDwbMiddleName}
      dwbLastName={props.dwbLastName}
      setDwbLastName={props.setDwbLastName}
      dwbSuffix={props.dwbSuffix}
      setDwbSuffix={props.setDwbSuffix}
      dwbAlias={props.dwbAlias}
      setDwbAlias={props.setDwbAlias}
    />
  );
}

function renderPgNameChangeFields(props) {
  const { endorsementType } = props;
  if (endorsementType === 'Name_Change_County') {
    return (
      <Autocomplete
        options={props.counties}
        loading={props.loadingCounties}
        value={
          props.endorsementToCounty
            ? props.counties.find((c) => c.value === props.endorsementToCounty) || null
            : null
        }
        onChange={(e, selected) => props.setEndorsementToCounty(selected ? selected.value : '')}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
        isOptionEqualToValue={(option, value) => option && value && option.value === value.value}
        renderInput={(params) => (
          <TextField
            {...params}
            label="To County"
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {props.loadingCounties ? <CircularProgress color="inherit" size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
        fullWidth
        disabled={props.loadingCounties}
        sx={{ mb: 3 }}
      />
    );
  }
  if (endorsementType === 'Name_Change_Deceased_Ward_Beneficiary') {
    return renderPgDwbFields(props);
  }
  if (
    endorsementType === 'Name_Change_Principal' &&
    getPrincipalPickerConfig(props.selectedBond?.bond_type) &&
    props.setEndorsementPrincipalId
  ) {
    return (
      <EndorsementPrincipalPickerFields
        selectedBondToEndorse={props.selectedBond}
        updatedName={props.updatedName}
        setUpdatedName={props.setUpdatedName}
        endorsementPrincipalId={props.endorsementPrincipalId}
        setEndorsementPrincipalId={props.setEndorsementPrincipalId}
      />
    );
  }
  return (
    <TextField
      label="Updated Name"
      name="updatedName"
      value={props.updatedName}
      onChange={(e) => props.setUpdatedName(e.target.value)}
      fullWidth
      required
      sx={{ mb: 3 }}
    />
  );
}

function renderPgEndorsementFormBody(props) {
  const { selectedBond, endorsementType } = props;
  return (
    <>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
        Endorsement Details
      </Typography>
      <Typography sx={{ mb: 1 }}>
        <strong>Bond Number:</strong> {selectedBond.bond_number ?? 'N/A'}
      </Typography>
      <Typography sx={{ mb: 2 }}>
        <strong>Bond Type:</strong> {selectedBond.bond_type}
      </Typography>

      <TextField
        label="Endorsement Type"
        select
        fullWidth
        value={endorsementType}
        onChange={(e) => props.setEndorsementType(e.target.value)}
        slotProps={{ select: { native: true } }}
        required
        sx={{ mb: 2 }}
      >
        {PG_BONDS_ENDORSEMENT_TYPE_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </TextField>

      {isNameChangeEndorsement(endorsementType)
        ? renderPgNameChangeFields(props)
        : (
          <TextField
            label="New Bond Amount (Rounded Up)"
            name="newBondAmount"
            value={props.newBondAmount}
            onChange={(e) => props.setNewBondAmount(e.target.value)}
            fullWidth
            required
            slotProps={{ input: { inputComponent: NumberFormatCustom } }}
            sx={{ mb: 3 }}
          />
        )}

      {(selectedBond.bond_type === 'Probate' || selectedBond.bond_type === 'Guardianship') && (
        <TextField
          label="Updated Estimated Assets (optional)"
          type="text"
          inputMode="decimal"
          value={props.estimatedAssets}
          onChange={(e) => props.setEstimatedAssets(e.target.value)}
          fullWidth
          placeholder="e.g. 250000"
          sx={{ mb: 2 }}
          helperText="Fill the updated estimated assets value if additional assets were discovered."
        />
      )}

      <Divider sx={{ mb: 2 }} />
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
        Documents
      </Typography>
      <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
        Acceptable Documentation:
      </Typography>
      <ul style={{ paddingLeft: '20px', marginBottom: '20px' }}>
        {getEndorsementInstructions(selectedBond).map((item) => (
          <li key={item} style={{ fontSize: '0.9rem', marginBottom: '6px' }}>
            {item}
          </li>
        ))}
      </ul>

      <Button
        variant="outlined"
        component="label"
        disabled={props.isSubmittingEndorsement}
        startIcon={<CloudUploadIcon />}
        sx={{ mb: 2 }}
      >
        {'Upload Supporting Document'}
        <input
          type="file"
          hidden
          accept=".pdf,.doc,.docx"
          onChange={props.onFileSelect}
        />
      </Button>
      {props.endorsementFile && (
        <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
          Selected File: {props.endorsementFile.name}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        Allowed file types: PDF, DOC, DOCX
      </Typography>

      <Divider sx={{ my: 2 }} />
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
        Contact Information
      </Typography>
      <TextField
        label=" Name"
        name="contactName"
        value={props.contactName}
        onChange={(e) => props.setContactName(e.target.value)}
        fullWidth
        required
        placeholder="Full name"
        sx={{ mb: 2 }}
      />
      <TextField
        label=" Email"
        type="email"
        name="contactEmail"
        value={props.contactEmail}
        onChange={(e) => props.setContactEmail(e.target.value)}
        fullWidth
        required
        placeholder="name@example.com"
        sx={{ mb: 1 }}
      />
    </>
  );
}

function renderPgEndorsementDialogContent(props) {
  const { selectedBond } = props;
  if (selectedBond?.bond_type === 'Blanket') {
    return (
      <Typography variant="h6" sx={{ py: 5, textAlign: 'center' }}>
        Endorsement not allowed for Blanket.
      </Typography>
    );
  }
  if (!selectedBond) {
    return (
      <Typography variant="h6" sx={{ py: 5, textAlign: 'center' }}>
        No bond selected.
      </Typography>
    );
  }
  return renderPgEndorsementFormBody(props);
}

export function PgBondsEndorsementDialog({
  open,
  onClose,
  selectedBond,
  endorsementType,
  setEndorsementType,
  newBondAmount,
  setNewBondAmount,
  endorsementFile,
  setEndorsementFile,
  isSubmittingEndorsement,
  contactName,
  setContactName,
  contactEmail,
  setContactEmail,
  updatedName,
  setUpdatedName,
  counties,
  endorsementToCounty,
  setEndorsementToCounty,
  loadingCounties,
  estimatedAssets,
  setEstimatedAssets,
  dwbFirstName,
  setDwbFirstName,
  dwbMiddleName,
  setDwbMiddleName,
  dwbLastName,
  setDwbLastName,
  dwbSuffix,
  setDwbSuffix,
  dwbAlias,
  setDwbAlias,
  endorsementPrincipalId,
  setEndorsementPrincipalId,
  role,
  onReleaseRider,
  onSubmitEndorsement,
  onFileSelect,
}) {
  const submitDisabled = isEndorsementSubmitDisabled({
    isSubmittingEndorsement,
    endorsementType,
    endorsementFile,
    endorsementToCounty,
    selectedBond,
    updatedName,
    dwbFirstName,
    dwbLastName,
  });

  return (
    <Dialog
      open={open}
      onClose={() => !isSubmittingEndorsement && onClose()}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 700, fontSize: '1.3rem' }}>Bond Endorsement</DialogTitle>
      <DialogContent dividers>
        {renderPgEndorsementDialogContent({
          selectedBond,
          endorsementType,
          setEndorsementType,
          newBondAmount,
          setNewBondAmount,
          endorsementFile,
          isSubmittingEndorsement,
          contactName,
          setContactName,
          contactEmail,
          setContactEmail,
          updatedName,
          setUpdatedName,
          counties,
          endorsementToCounty,
          setEndorsementToCounty,
          loadingCounties,
          estimatedAssets,
          setEstimatedAssets,
          dwbFirstName,
          setDwbFirstName,
          dwbMiddleName,
          setDwbMiddleName,
          dwbLastName,
          setDwbLastName,
          dwbSuffix,
          setDwbSuffix,
          dwbAlias,
          setDwbAlias,
          endorsementPrincipalId,
          setEndorsementPrincipalId,
          onFileSelect,
        })}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmittingEndorsement}>
          Close
        </Button>
        {selectedBond && selectedBond.bond_type !== 'Blanket' && (
          <>
            {(role === 'internal_user' || role === 'super_user') && (
              <Button
                variant="outlined"
                color="secondary"
                disabled={!selectedBond || isSubmittingEndorsement}
                onClick={onReleaseRider}
              >
                Release Rider
              </Button>
            )}
            {role !== 'internal_user' && role !== 'super_user' && (
              <Button
                variant="contained"
                disabled={submitDisabled}
                onClick={onSubmitEndorsement}
              >
                {isSubmittingEndorsement ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Submit Endorsement'
                )}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export function validatePgBondDocumentFile(file, onError) {
  if (file && !PG_BONDS_VALID_DOCUMENT_TYPES.includes(file.type)) {
    onError('Only PDF or Word documents are allowed.');
    return false;
  }
  return true;
}

export { getBondUpdateUrl } from './pgBondsTableConfig';
